// The publish/subscribe surface modules, the renderer, and server-sync hook into.
//
// Typed by an event map (event name → payload type) the game passes in: `new EventBus<GameEvents>()`, or
// `createGame<GameEvents>(...)`. On a typed bus, emitting or listening to a name the map lacks, or with the wrong
// payload, is a type error. Without a map the bus stays open (any name, payload typed at the call site), which is how
// spike 001 uses it; a typed bus is the same object at run time, so the two mix freely.

/** A listener for one event's payload. */
export type EventListener<T = unknown> = (payload: T) => void;

/** The open event map: any name, any payload. The default for an untyped bus. */
export type OpenEvents = Record<string, unknown>;

/** True when `M` is open (has a string index), false for a closed map of known names. */
export type IsOpenEvents<M> = string extends keyof M ? true : false;

/** The names a closed map knows. */
export type EventName<M> = keyof M & string;

/** The payload argument: optional when the event's payload may be undefined (`"pause": void`), required otherwise. */
export type EventPayloadArgs<M, K extends keyof M> = undefined extends M[K] ? [payload?: M[K]] : [payload: M[K]];

/** `on` for a bus over map `M`: open buses keep the old `on<T>(name: string, ...)`; closed ones check the name and payload. */
export type EventOn<M> =
  IsOpenEvents<M> extends true
    ? <T = unknown>(event: string, listener: EventListener<T>) => () => void
    : <K extends EventName<M>>(event: K, listener: EventListener<M[K]>) => () => void;

/** `emit` for a bus over map `M`, as {@link EventOn}. */
export type EventEmit<M> =
  IsOpenEvents<M> extends true
    ? <T = unknown>(event: string, payload: T) => void
    : <K extends EventName<M>>(event: K, ...args: EventPayloadArgs<M, K>) => void;

/** The event bus. `M` maps event names to payload types; leave it out for an open bus. */
export class EventBus<M extends object = OpenEvents> {
  private readonly listeners = new Map<string, Set<EventListener>>();

  /** Listen to an event; returns the unsubscribe function. */
  readonly on: EventOn<M> = ((event: string, listener: EventListener) => {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener);
    return () => {
      set!.delete(listener);
    };
  }) as EventOn<M>;

  /** Call every listener of an event, in subscription order. */
  readonly emit: EventEmit<M> = ((event: string, payload?: unknown) => {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of set) listener(payload);
  }) as EventEmit<M>;

  /** How many listeners an event has (the harness and tests read it). */
  listenerCount(event: EventName<M> | (IsOpenEvents<M> extends true ? string : never)): number {
    return this.listeners.get(event)?.size ?? 0;
  }
}

/**
 * Declare a game's event names once, as a typed list a module or the harness can read at run time:
 * `const EVENTS = eventNames<GameEvents>()("player:land", "combat:hit")`. Naming an event the map lacks is a type
 * error, and the list must name every event the map has.
 */
export function eventNames<M extends object>() {
  return <const N extends readonly EventName<M>[]>(...names: N & ([EventName<M>] extends [N[number]] ? unknown : { missing: Exclude<EventName<M>, N[number]> })): readonly EventName<M>[] => names;
}
